import { useState, useEffect } from 'react';

/**
 * Utility functions to determine team availability status
 */

/**
 * Check if current time is within business hours
 * @param {string} timezone - Timezone identifier (e.g., 'Asia/Kolkata')
 * @param {number} startHour - Start hour in 24-hour format
 * @param {number} endHour - End hour in 24-hour format
 * @returns {boolean} - True if currently within business hours
 */
export const isWithinBusinessHours = (timezone = 'Asia/Kolkata', startHour = 9, endHour = 18) => {
  try {
    const now = new Date();
    const options = { 
      timeZone: timezone, 
      hour: 'numeric', 
      hour12: false,
      weekday: 'short'
    };
    
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(now);
    
    const currentHour = parseInt(parts.find(part => part.type === 'hour')?.value || '0');
    const currentDay = parts.find(part => part.type === 'weekday')?.value;
    
    // Check if it's a weekend
    const isWeekend = currentDay === 'Sat' || currentDay === 'Sun';
    
    // Within business hours on weekdays
    return !isWeekend && currentHour >= startHour && currentHour < endHour;
  } catch (error) {
    console.error('Error checking business hours:', error);
    return true; // Default to available if there's an error
  }
};

/**
 * Get team availability status
 * @returns {Object} - Object containing availability status for different teams
 */
export const getTeamAvailability = () => {
  return {
    liveChat: {
      isOnline: true, // 24/7 available
      status: 'online',
      label: 'Online Now',
      hours: '24/7 Available'
    },
    whatsapp: {
      isOnline: isWithinBusinessHours('Asia/Kolkata', 9, 18),
      status: isWithinBusinessHours('Asia/Kolkata', 9, 18) ? 'online' : 'offline',
      label: isWithinBusinessHours('Asia/Kolkata', 9, 18) ? 'Online Now' : 'Offline',
      hours: '9 AM - 6 PM IST'
    },
    ticket: {
      isOnline: true, // 24/7 available
      status: 'online',
      label: 'Online Now',
      hours: '24/7 Available'
    },
    email: {
      isOnline: true, // 24/7 available
      status: 'online',
      label: 'Online Now',
      hours: '24/7 Available'
    },
    phone: {
      isOnline: isWithinBusinessHours('Asia/Kolkata', 9, 18),
      status: isWithinBusinessHours('Asia/Kolkata', 9, 18) ? 'online' : 'offline',
      label: isWithinBusinessHours('Asia/Kolkata', 9, 18) ? 'Online Now' : 'Offline',
      hours: '9 AM - 6 PM IST'
    },
    sales: {
      isOnline: isWithinBusinessHours('Asia/Kolkata', 9, 18),
      status: isWithinBusinessHours('Asia/Kolkata', 9, 18) ? 'online' : 'offline',
      label: isWithinBusinessHours('Asia/Kolkata', 9, 18) ? 'Online Now' : 'Offline',
      hours: '9 AM - 6 PM IST (Mon-Fri)'
    }
  };
};

/**
 * Custom hook to use team availability with real-time updates
 */
export const useTeamAvailability = () => {
  const [availability, setAvailability] = useState(getTeamAvailability());

  useEffect(() => {
    // Update availability every minute
    const interval = setInterval(() => {
      setAvailability(getTeamAvailability());
    }, 60000); // Update every 60 seconds

    return () => clearInterval(interval);
  }, []);

  return availability;
};

// For non-React usage
const teamAvailabilityUtils = {
  isWithinBusinessHours,
  getTeamAvailability
};
export default teamAvailabilityUtils;
